import { useMemo, useState, type ReactNode } from "react";
import { Tooltip, message } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

import { Button, Input, Select, Modal } from "../../../../shared";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { useCurrencySymbol } from "../../../../../hooks/useCurrencySymbol";
import FeatureCard from "./FeatureCard";
import {
  DEFAULT_BIG_WINNERS_CONFIG,
  isBigWinnersConfigEqual,
  isBigWinnersConfigValid,
  parseNonNegativeInt,
  type BigWinnersConfig,
  type DurationUnit,
} from "../bigWinnersConfig";
import type { BigWinnersRefreshResult } from "../../../../../queries/casino-configuration/features/useBigWinnersConfig";

export interface BigWinnersCardProps {
  /** Whether the card starts expanded. */
  defaultExpanded?: boolean;
  /** Seed config (defaults to the empty/default shape). */
  initialConfig?: BigWinnersConfig;
  /**
   * Persist handler. May return the saved config so the card can reflect any
   * server-side clamping; when it returns nothing the submitted config is kept.
   */
  onSave?: (
    config: BigWinnersConfig,
  ) => Promise<BigWinnersConfig | void> | BigWinnersConfig | void;
  /**
   * Manual "refresh now" handler. Returns the refresh result so the card can
   * report how many cards were produced (or that the recompute was skipped).
   */
  onRefresh?: () =>
    | Promise<BigWinnersRefreshResult | void>
    | BigWinnersRefreshResult
    | void;
}

const DURATION_UNIT_OPTIONS: { value: DurationUnit; label: string }[] = [
  { value: "hours", label: AppStrings.CASINO_CONFIG_FEATURES_UNIT_HOURS },
  { value: "days", label: AppStrings.CASINO_CONFIG_FEATURES_UNIT_DAYS },
];

function FieldLabel({
  label,
  hint,
  htmlFor,
}: {
  label: string;
  hint: string;
  htmlFor?: string;
}) {
  return (
    <label className="feature-field-label" htmlFor={htmlFor}>
      <span>{label}</span>
      <Tooltip title={hint}>
        <InfoCircleOutlined
          className="feature-field-hint-icon"
          aria-label={hint}
        />
      </Tooltip>
    </label>
  );
}

function numberToInputValue(value: number | null): string {
  return value === null ? "" : String(value);
}

/**
 * Big Winners feature card — header (name + description + enable toggle +
 * chevron) provided by `FeatureCard`, with the tuning-params form in the
 * collapsible body.
 */
function BigWinnersCard({
  defaultExpanded = false,
  initialConfig = DEFAULT_BIG_WINNERS_CONFIG,
  onSave,
  onRefresh,
}: BigWinnersCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [baseline, setBaseline] = useState<BigWinnersConfig>(initialConfig);
  const [config, setConfig] = useState<BigWinnersConfig>(initialConfig);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // In-flight flag while the enable/disable toggle is being persisted.
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  // Target enabled value awaiting confirmation; `null` means no modal is open.
  // The toggle is controlled, so cancelling simply drops this and the switch
  // snaps back to `config.enabled`.
  const [pendingEnable, setPendingEnable] = useState<boolean | null>(null);
  // Operator's platform currency; "$" is the documented consumer fallback.
  const currencySymbol = useCurrencySymbol() ?? "$";

  const dirty = useMemo(
    () => !isBigWinnersConfigEqual(config, baseline),
    [config, baseline],
  );
  const valid = useMemo(() => isBigWinnersConfigValid(config), [config]);
  // Block Save while a toggle persist is in flight so the two requests can't
  // race and leave baseline out of sync with what the server accepted.
  const canSave = dirty && valid && !saving && !togglingEnabled;

  const patch = (next: Partial<BigWinnersConfig>) =>
    setConfig((prev) => ({ ...prev, ...next }));

  const handleReset = () => setConfig(baseline);

  // Toggling the enable switch requests confirmation instead of applying the
  // change immediately, guarding against accidental enable/disable.
  const requestEnabledChange = (next: boolean) => {
    if (next === config.enabled) return;
    setPendingEnable(next);
  };

  const cancelEnabledChange = () => setPendingEnable(null);

  // Persist against `baseline` (not `config`) so an unsaved tuning-param edit
  // isn't force-saved just because the operator flipped the enable toggle.
  const confirmEnabledChange = async () => {
    if (pendingEnable === null) return;
    const next = pendingEnable;
    const previousEnabled = config.enabled;
    setPendingEnable(null);
    if (next === previousEnabled) return;

    setConfig((prev) => ({ ...prev, enabled: next }));
    setTogglingEnabled(true);
    try {
      const saved = await onSave?.({ ...baseline, enabled: next });
      const persisted = saved ?? { ...baseline, enabled: next };
      setBaseline(persisted);
      setConfig((prev) => ({ ...prev, enabled: persisted.enabled }));
      message.success(
        next
          ? AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_ENABLE_SUCCESS
          : AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_DISABLE_SUCCESS,
      );
    } catch (err) {
      setConfig((prev) => ({ ...prev, enabled: previousEnabled }));
      message.error(
        (err as Error)?.message ||
          (next
            ? AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_ENABLE_ERROR
            : AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_DISABLE_ERROR),
      );
    } finally {
      setTogglingEnabled(false);
    }
  };

  const isEnabling = pendingEnable === true;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // The backend clamps out-of-range values and echoes what it saved, so
      // adopt the returned config (falling back to the submitted one) to keep
      // the form in sync with what was actually persisted.
      const saved = await onSave?.(config);
      const persisted = saved ?? config;
      setBaseline(persisted);
      setConfig(persisted);
      message.success(AppStrings.CASINO_CONFIG_FEATURES_SAVE_SUCCESS);
    } catch (err) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_FEATURES_SAVE_ERROR,
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await onRefresh?.();
      // No result means onRefresh was not provided or returned void — there is
      // nothing to report, so avoid a misleading "0 cards generated" toast.
      if (!result) return;
      if (result.skipped) {
        message.info(
          AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_SKIPPED,
        );
      } else {
        message.success(
          `${AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_SUCCESS}: ${result.count} ${AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_CARDS}`,
        );
      }
    } catch (err) {
      message.error(
        (err as Error)?.message ||
          AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_ERROR,
      );
    } finally {
      setRefreshing(false);
    }
  };

  const renderNumberField = (
    field: keyof Pick<
      BigWinnersConfig,
      "threshold" | "maxItems" | "timeWindowValue" | "refreshIntervalValue"
    >,
    options: {
      id: string;
      label: string;
      hint: string;
      placeholder: string;
      prefix?: ReactNode;
      unit?: {
        value: DurationUnit;
        onChange: (next: DurationUnit) => void;
        options: { value: DurationUnit; label: string }[];
      };
    },
  ) => (
    <div className="feature-field">
      <FieldLabel label={options.label} hint={options.hint} htmlFor={options.id} />
      <div
        className={`feature-field-control ${
          options.unit ? "feature-field-control--with-unit" : ""
        }`}
      >
        <Input
          id={options.id}
          type="number"
          min={0}
          inputMode="numeric"
          prefix={options.prefix}
          placeholder={options.placeholder}
          value={numberToInputValue(config[field])}
          onChange={(e) => patch({ [field]: parseNonNegativeInt(e.target.value) })}
          data-testid={`big-winners-${options.id}`}
        />
        {options.unit ? (
          <Select<DurationUnit>
            className="feature-field-unit"
            value={options.unit.value}
            onChange={options.unit.onChange}
            options={options.unit.options}
            data-testid={`big-winners-${options.id}-unit`}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <>
    <FeatureCard
      cardId="big-winners"
      name={AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_NAME}
      description={
        AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_DESCRIPTION
      }
      enabled={pendingEnable ?? config.enabled}
      onEnabledChange={requestEnabledChange}
      enableDisabled={saving || togglingEnabled || pendingEnable !== null}
      enableLoading={togglingEnabled}
      expanded={expanded}
      onToggleExpand={() => setExpanded((prev) => !prev)}
    >
      <div className="feature-field-grid">
        {renderNumberField("threshold", {
          id: "threshold",
          label:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_THRESHOLD_LABEL,
          hint: AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_THRESHOLD_HINT,
          placeholder:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_THRESHOLD_PLACEHOLDER,
          prefix: currencySymbol,
        })}

        {renderNumberField("maxItems", {
          id: "max-items",
          label:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_MAX_ITEMS_LABEL,
          hint: AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_MAX_ITEMS_HINT,
          placeholder:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_MAX_ITEMS_PLACEHOLDER,
        })}

        {renderNumberField("timeWindowValue", {
          id: "time-window",
          label:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_TIME_WINDOW_LABEL,
          hint: AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_TIME_WINDOW_HINT,
          placeholder:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_TIME_WINDOW_PLACEHOLDER,
          unit: {
            value: config.timeWindowUnit,
            onChange: (next) => patch({ timeWindowUnit: next }),
            options: DURATION_UNIT_OPTIONS,
          },
        })}

        {renderNumberField("refreshIntervalValue", {
          id: "refresh-interval",
          label: AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_LABEL,
          hint: AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_HINT,
          placeholder:
            AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_PLACEHOLDER,
          unit: {
            value: config.refreshIntervalUnit,
            onChange: (next) => patch({ refreshIntervalUnit: next }),
            options: DURATION_UNIT_OPTIONS,
          },
        })}
      </div>

      <div className="feature-card-actions">
        <Button
          intent="secondary"
          onClick={handleRefresh}
          // Refresh acts on the persisted config, so only allow it when the
          // feature is saved-on and there are no unsaved changes to the toggle
          // (otherwise the backend would recompute against stale state / skip).
          disabled={
            !baseline.enabled ||
            config.enabled !== baseline.enabled ||
            refreshing ||
            saving
          }
          loading={refreshing}
          data-testid="big-winners-refresh"
        >
          {AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_REFRESH_BTN}
        </Button>
        <Button
          intent="secondary"
          onClick={handleReset}
          disabled={!dirty || saving}
          data-testid="big-winners-reset"
        >
          {AppStrings.CASINO_CONFIG_FEATURES_RESET}
        </Button>
        <Button
          intent="primary"
          onClick={handleSave}
          disabled={!canSave}
          loading={saving}
          data-testid="big-winners-save"
        >
          {AppStrings.CASINO_CONFIG_FEATURES_SAVE}
        </Button>
      </div>
    </FeatureCard>

      <Modal
        open={pendingEnable !== null}
        onClose={cancelEnabledChange}
        size="sm"
        title={
          isEnabling
            ? AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_ENABLE_CONFIRM_TITLE
            : AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_DISABLE_CONFIRM_TITLE
        }
        primaryAction={{
          label: isEnabling
            ? AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_ENABLE_CONFIRM_BTN
            : AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_DISABLE_CONFIRM_BTN,
          onClick: confirmEnabledChange,
        }}
        secondaryAction={{
          label: AppStrings.CASINO_CONFIG_FEATURES_CONFIRM_CANCEL,
          onClick: cancelEnabledChange,
        }}
        data-testid="big-winners-confirm-modal"
      >
        <p className="feature-confirm-text">
          {isEnabling
            ? AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_ENABLE_CONFIRM_DESC
            : AppStrings.CASINO_CONFIG_FEATURES_BIG_WINNERS_DISABLE_CONFIRM_DESC}
        </p>
      </Modal>
    </>
  );
}

export default BigWinnersCard;
